import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ReportProblem as AlertIcon } from '@mui/icons-material';
import api from '../services/api';

interface PausedStrategy {
  id: number;
  name: string;
}

interface ActiveCBEvent {
  active: boolean;
  triggered_at: string | null;
  daily_pnl: number;
  account_balance: number;
  loss_percentage: number;
  open_positions_count: number;
  paused_strategies: PausedStrategy[];
  reset_info: string;
}

const SESSION_KEY = 'cb_alert_dismissed';

const CircuitBreakerAlertModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ActiveCBEvent | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const check = async () => {
      try {
        const res = await api.get('/api/v1/circuit-breaker/active-event');
        const event: ActiveCBEvent = res.data;
        if (event.active) {
          setData(event);
          setOpen(true);
        }
      } catch {
        // Silent fail — never block the app
      }
    };
    check();
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  if (!data) return null;

  const lossPct = (data.loss_percentage * 100).toFixed(1);
  const triggeredDate = data.triggered_at
    ? new Date(data.triggered_at).toLocaleString()
    : 'Unknown';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          border: '2px solid #C0392B',
          backgroundColor: '#FFF5F5',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AlertIcon sx={{ color: '#C0392B' }} />
        Safety Shutoff Triggered
        <Chip
          label="TRADING PAUSED"
          size="small"
          sx={{
            ml: 1,
            backgroundColor: '#C0392B',
            color: '#fff',
            fontWeight: 600,
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, color: '#5A3A3A' }}>
          Your circuit breaker tripped on <strong>{triggeredDate}</strong> after
          a daily loss of <strong>${Math.abs(data.daily_pnl).toFixed(2)}</strong>{' '}
          (<strong>{lossPct}%</strong> of your ${data.account_balance.toFixed(2)} balance).
        </Typography>

        {data.paused_strategies.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#5A3A3A', mb: 0.5 }}>
              Paused strategies ({data.paused_strategies.length}):
            </Typography>
            <List dense disablePadding>
              {data.paused_strategies.map((s) => (
                <ListItem key={s.id} sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={s.name}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {data.open_positions_count > 0 && (
          <Typography variant="body2" sx={{ mb: 2, color: '#7A4A4A' }}>
            You still have <strong>{data.open_positions_count}</strong> open
            position{data.open_positions_count !== 1 ? 's' : ''} that were not
            closed by the circuit breaker.
          </Typography>
        )}

        <Box
          sx={{
            p: 2,
            backgroundColor: 'rgba(192,57,43,0.08)',
            borderRadius: 1,
            border: '1px solid rgba(192,57,43,0.2)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#6A4040' }}>
            {data.reset_info}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleDismiss}
          variant="contained"
          sx={{
            backgroundColor: '#3A8C8C',
            '&:hover': { backgroundColor: '#2D6F6F' },
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CircuitBreakerAlertModal;
